<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="distribution-page">
            <div class="header-fixed">
                <!-- 头部信息 -->
                <div class="header-section">
                    <div class="school-info">
                        <div class="school-name">{{ showClass.school_name }}</div>
                        <div class="class-name">
                            <span>{{ showClass.name }}</span>
                            <van-icon name="exchange" class="cycle-icon" @click="showSelectClassModal = true" />
                        </div>
                    </div>
                    <van-button class="date-btn" @click="showDatePicker = true">
                        <van-icon :name="require('@/assets/icons/calendar.svg')" />
                        <span>{{ selectedDate }}</span>
                    </van-button>
                </div>

                <!-- 搜索栏 -->
                <div class="search-section">
                    <van-search v-model="searchKeyword" placeholder="搜索学生姓名" class="search-bar" @search="onSearch" />
                </div>
            </div>
            <!-- 学生列表 -->
            <!-- 空状态 -->
            <div v-if="students.length === 0" class="empty-state">
                <van-empty description="暂无发放记录" />
            </div>
            <div v-else>
                <div class="student-list">
                    <div :class="['student-item', student.selected ? 'selected' : '']" v-for="student in students"
                        :key="student.id">
                        <van-checkbox shape="square" v-model="student.selected" class="student-checkbox" :disabled="student.state === '已取消'"
                            @change="updateSelection" />
                        <van-icon
                            :name="student.product_category_name === '鲜奶' ? require('@/assets/icons/milk.svg') : require('@/assets/icons/yogurt.svg')"
                            class="item-icon" />
                        <div class="student-info">
                            <div class="student-name">{{ student.partner_name }}</div>
                            <div class="item-type" :class="student.product_category_name === '鲜奶' ? 'milk' : 'yogurt'">
                                {{ student.product_category_name }}
                            </div>
                        </div>
                        <div class="status-btn" :class="getStatusText(student.state)">
                            {{ student.state }}
                        </div>
                    </div>
                </div>
            </div>
            <!-- 底部确认按钮 -->
            <div class="footer-section">
                <van-button type="primary" class="confirm-btn" @click="confirmNotCollected"
                    :disabled="selectedCount === 0">
                    确认未取货
                </van-button>
                <div class="selection-info">
                    已选择{{ selectedCount }}人,点击确认后将标记为已取消
                </div>
            </div>
            <!-- 日期选择器 -->
            <van-popup v-model:show="showDatePicker" position="bottom" round>
                <van-datetime-picker v-model="currentDate" type="date" title="选择日期" :min-date="minDate"
                    :max-date="maxDate" @confirm="onDateConfirm" @cancel="showDatePicker = false" />
            </van-popup>

            <!-- 选择班级弹框 -->
            <van-popup v-model:show="showSelectClassModal" position="center" round :style="{ width: '90%' }">
                <div class="select-class-modal">
                    <div class="modal-header">
                        <span class="modal-title">选择班级</span>
                    </div>

                    <div class="modal-content">
                        <!-- 选择学校 -->
                        <div class="select-section">
                            <div class="section-title">学校</div>
                            <van-field v-if="selectedClass" v-model="selectedClass.school_name" placeholder="请选择学校" readonly right-icon="arrow" class="select-field" />
                        </div>

                        <!-- 选择年级 -->
                        <div class="select-section">
                            <div class="section-title">年级</div>
                            <div class="grade-grid">
                                <div class="grade-item active" v-if="selectedClass" >
                                    {{ selectedClass?.grade_name }}
                                </div>
                            </div>
                        </div>

                        <!-- 选择班级 -->
                        <div class="select-section">
                            <div class="section-title">选择班级</div>
                            <div class="class-grid">
                                <div v-for="classItem in classOptions" :key="classItem.id" class="class-item"
                                    :class="{ active: selectedClass?.id === classItem.id }" @click="selectClass(classItem)">
                                    {{ classItem.name }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <van-button class="cancel-btn" @click="showSelectClassModal = false">取消</van-button>
                        <van-button class="confirm-btn" type="primary" @click="confirmSelectClass">确认</van-button>
                    </div>
                </div>
            </van-popup>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { Toast, Dialog } from 'vant'

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 格式化日期
const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}/${month}/${day}`
}

// 日期相关
const showDatePicker = ref(false)
const currentDate = ref(new Date())
const selectedDate = ref(formatDate(new Date()))
const minDate = new Date(new Date().getFullYear() - 1, 0, 1)
const maxDate = new Date(new Date().getFullYear() + 1, 11, 31)

// 搜索关键词
const searchKeyword = ref('')

// 学生数据
const students = ref([])

// 切换班级相关
const showSelectClassModal = ref(false)
const selectedClass = ref(null)
const showClass = ref(null)
const classOptions = ref([])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

onMounted(() => {
    setWxTitle('今日发放名单')
    initPage()
})

// 页面初始化
const initPage = async (text) => {
    try {
        // 调用接口获取发放名单数据
        const params = {
            partner_id: parseInt(localStorage.getItem('userId')),
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            date: selectedDate.value.replace(/\//g, '-')
        }
        if (text) {
            params.student_name = text
        }
        if (selectedClass.value) {
            params.class_id = selectedClass.value.id
        }
        const res = await post('/api/today/distribution', params)
        if (res.result && res.result.state === 'ok') {
            if (res.result.class_list.length > 0) {
                classOptions.value = res.result.class_list || []
            }
            selectedClass.value = res.result.selected_class_info
            showClass.value = res.result.selected_class_info
            students.value = res.result.order_arr || []
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
    }
}

// 选中的学生数量
const selectedCount = computed(() => {
    return students.value.filter(student => student.selected).length
})

// 日期确认
const onDateConfirm = async (value) => {
    selectedDate.value = formatDate(value)
    showDatePicker.value = false
    initPage()
}

// 搜索
const onSearch = () => {
    initPage(searchKeyword.value)
}

// 获取状态文本
const getStatusText = (status) => {
    const statusMap = {
        '已取货': 'collected',
        '已取消': 'canceled',
        '未取货': 'notCollected'
    }
    return statusMap[status] || ''
}

// 更新选择状态
const updateSelection = () => {
    // 可以在这里添加选择逻辑
    console.log('选择状态更新')
}

// 班级选择器确认
const selectClass = (classItem) => {
    selectedClass.value = classItem
}

// 确认选择班级
const confirmSelectClass = async () => {
    showSelectClassModal.value = false
    initPage(searchKeyword.value)
    showClass.value = selectedClass.value
}

// 确认未取货
const confirmNotCollected = async () => {
    const selectedStudents = students.value.filter(student => student.selected)

    if (selectedStudents.length === 0) {
        Toast('请选择要标记的学生')
        return
    }

    try {
        const res = await post('/api/order/confirm/no/pickup', {
            order_ids: selectedStudents.map(student => student.id),
        })

        if (res.result && res.result.state === 'ok') {
            Toast.success('操作成功')
            // 更新本地状态
            selectedStudents.forEach(student => {
                student.state = '已取消'
                student.selected = false
            })
        } else {
            Dialog.alert({
                title: '操作失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('确认未取货失败:', error)
        Dialog.alert({
            title: '操作失败',
            message: '网络错误，请重试'
        })
    }
}
</script>

<style scoped>
.distribution-page {
    background: #fff;
    padding: 16px;
    min-height: calc(100vh - 32px);
}

.header-fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #fff;
    z-index: 100;
    padding: 16px;
}

.header-section {
    background: #FEF2F2;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.student-list {
    padding-top: 140px;
    padding-bottom: 110px;
}

.school-info {
    flex: 1;
}

.school-name {
    font-size: 16px;
    font-weight: 500;
    color: #000000;
    margin-bottom: 4px;
}

.class-name {
    font-size: 14px;
    color: #4B5563;
}

.date-btn {
    background: #fff;
    color: #4B5563;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    height: 34px;
}

.date-btn :deep(.van-button__text) {
    display: flex;
    align-items: center;
    gap: 4px;
}

.search-bar {
    background: #fff;
    padding: 0;
}

.search-bar :deep(.van-search__content) {
    background: #fff;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
}

.student-item {
    background: #fff;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.student-item.selected {
    background: #FEF2F2;
}

.student-checkbox {
    flex-shrink: 0;
}

:deep(.van-checkbox__icon .van-icon) {
    border-radius: 5px;
}

.item-icon,
.item-icon :deep(.van-icon__image) {
    width: 20px;
    height: 20px;
}

.student-info {
    flex: 1;
}

.student-name {
    font-size: 16px;
    font-weight: 500;
    color: #000000;
    margin-bottom: 4px;
}

.item-type {
    font-size: 14px;
}

.item-type.milk {
    color: #48BBEC;
}

.item-type.yogurt {
    color: #EC4899;
}

.status-btn {
    padding: 6px 10px;
    border-radius: 9999px;
    font-size: 14px;
}

.status-btn.collected {
    background: #DCFCE7;
    color: #16A34A;
}

.status-btn.canceled {
    background: #555453;
    color: #B1B5B3;
}

.status-btn.notCollected {
    background: #FEF9C3;
    color: #CA8A04;
}

.footer-section {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 16px;
    border-top: 1px solid #f0f0f0;
    z-index: 100;
}

.confirm-btn {
    width: 100%;
    height: 52px;
    border-radius: 4px;
    font-size: 18px;
    font-weight: 500;
}

.selection-info {
    text-align: center;
    font-size: 14px;
    color: #6B7280;
    margin: 8px 0;
}

.empty-state {
    padding-top: 100px;
    text-align: center;
}

.cycle-icon {
    margin-left: 8px;
    color: var(--van-primary-color);
    font-size: 16px;
    font-weight: 500;
}

/* 选择班级弹框样式 */
.select-class-modal {
    padding: 20px;
}

.modal-header {
    text-align: center;
    margin-bottom: 20px;
}

.modal-title {
    font-size: 18px;
    font-weight: 500;
    color: #000;
}

.modal-content {
    margin-bottom: 20px;
}

.select-section {
    margin-bottom: 20px;
}

.section-title {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 12px;
}

.grade-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.grade-item {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #374151;
    background: #f3f4f6;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
}

.grade-item.active {
    background: var(--van-primary-color);
    color: white;
}

.class-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.class-item {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #374151;
    background: #f3f4f6;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
}

.class-item.active {
    background: var(--van-primary-color);
    color: white;
}

.modal-footer {
    display: flex;
    gap: 12px;
}

.select-class-modal .cancel-btn,
.select-class-modal .confirm-btn {
    flex: 1;
    height: 40px;
    border-radius: 6px;
    font-size: 14px;
    border: none;
}

.cancel-btn {
    background: #f5f5f5;
    color: #6B7280;
    border: 1px solid #e5e7eb;
}

</style>